<template>
  <section
    ref="sectionEl"
    id="use-cases"
    class="relative py-28 bg-gray-950 overflow-hidden"
    :class="{ 'is-visible': isVisible }"
  >
    <!-- Ambient glows -->
    <div class="absolute inset-0 pointer-events-none">
      <div class="particle" style="left:8%; animation-duration:14s; animation-delay:0s"></div>
      <div class="particle" style="left:25%; animation-duration:18s; animation-delay:2s"></div>
      <div class="particle" style="left:50%; animation-duration:16s; animation-delay:1s"></div>
      <div class="particle" style="left:72%; animation-duration:20s; animation-delay:3s"></div>
      <div class="particle" style="left:88%; animation-duration:15s; animation-delay:5s"></div>
      <div class="absolute top-0 left-1/3 w-96 h-96 rounded-full bg-blue-500/[0.04] blur-3xl"></div>
      <div class="absolute bottom-0 right-1/4 w-80 h-80 rounded-full bg-purple-500/[0.03] blur-3xl"></div>
    </div>

    <div class="container mx-auto px-4 relative z-10">

      <!-- Header -->
      <div class="max-w-4xl mx-auto text-center mb-16 anim-el d1">
        <p class="text-[13px] uppercase tracking-[0.2em] text-blue-400/70 font-medium mb-4">
          Risultati Reali
        </p>
        <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
          {{ t('useCases.title') }}
        </h2>
        <p class="text-lg text-white/60 max-w-2xl mx-auto leading-relaxed">
          {{ t('useCases.subtitle') }}
        </p>
      </div>

      <!-- Impact metrics row -->
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto mb-16">
        <div class="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-6 text-center anim-el d2">
          <p class="text-4xl md:text-5xl font-extrabold text-white mb-1 tabular-nums">+{{ cTraffic }}%</p>
          <p class="text-sm font-semibold text-white/70 mb-1">Traffico organico</p>
          <p class="text-xs text-white/40">Crescita SEO media clienti</p>
        </div>
        <div class="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-6 text-center anim-el d3">
          <p class="text-4xl md:text-5xl font-extrabold text-blue-400 mb-1 tabular-nums">-{{ cCost }}%</p>
          <p class="text-sm font-semibold text-white/70 mb-1">Costo del supporto</p>
          <p class="text-xs text-white/40">Riduzione con chatbot AI</p>
        </div>
        <div class="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-6 text-center anim-el d4">
          <p class="text-4xl md:text-5xl font-extrabold text-white mb-1 tabular-nums">{{ cChats }}k+</p>
          <p class="text-sm font-semibold text-white/70 mb-1">Conversazioni/mese</p>
          <p class="text-xs text-white/40">Gestite dai nostri chatbot</p>
        </div>
      </div>

      <!-- TisaLabs case study card -->
      <div class="max-w-5xl mx-auto anim-el d5">
        <div class="rounded-2xl border border-white/[0.08] bg-white/[0.02] overflow-hidden">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-0">

            <!-- Left: Company info + quote -->
            <div class="p-8 md:p-10 border-b lg:border-b-0 lg:border-r border-white/[0.06]">
              <img
                src="../assets/img/tisalabs_logo.png"
                alt="TisaLabs Logo"
                class="h-9 w-auto object-contain brightness-0 invert mb-6"
              />

              <div class="flex items-center gap-2 mb-5">
                <span class="px-2.5 py-1 rounded-md bg-blue-500/10 border border-blue-500/20 text-blue-400 text-[11px] font-semibold">Chatbot AI</span>
                <span class="px-2.5 py-1 rounded-md bg-green-500/10 border border-green-500/20 text-green-400 text-[11px] font-semibold">SEO</span>
                <span class="px-2.5 py-1 rounded-md bg-purple-500/10 border border-purple-500/20 text-purple-400 text-[11px] font-semibold">Content AI</span>
              </div>

              <h3 class="text-2xl font-bold text-white mb-4">TisaLabs</h3>
              <p class="text-white/60 leading-relaxed mb-6">
                {{ t('useCases.tisalabs.description') }}
              </p>

              <!-- Key innovations -->
              <div class="space-y-3">
                <div v-for="innovation in innovations" :key="innovation.key" class="flex items-start gap-3">
                  <div class="w-8 h-8 rounded-lg bg-white/[0.04] border border-white/[0.06] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <component :is="innovation.icon" class="h-4 w-4 text-blue-400" />
                  </div>
                  <div>
                    <p class="text-sm font-semibold text-white mb-0.5">{{ t(`useCases.tisalabs.innovations.${innovation.key}.title`) }}</p>
                    <p class="text-xs text-white/50 leading-relaxed">{{ t(`useCases.tisalabs.innovations.${innovation.key}.description`) }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right: Metrics + mini mockup -->
            <div class="p-8 md:p-10">
              <!-- Metric tiles -->
              <div class="grid grid-cols-2 gap-3 mb-6">
                <div class="rounded-xl bg-white/[0.03] border border-white/[0.06] p-4">
                  <p class="text-[10px] text-white/40 mb-1">Traffico organico</p>
                  <p class="text-2xl font-bold text-white">+340%</p>
                  <p class="text-[10px] text-green-400 mt-0.5">↑ in 4 mesi</p>
                </div>
                <div class="rounded-xl bg-white/[0.03] border border-white/[0.06] p-4">
                  <p class="text-[10px] text-white/40 mb-1">Supporto manuale</p>
                  <p class="text-2xl font-bold text-blue-400">-65%</p>
                  <p class="text-[10px] text-blue-400/70 mt-0.5">ore risparmiate</p>
                </div>
                <div class="rounded-xl bg-white/[0.03] border border-white/[0.06] p-4">
                  <p class="text-[10px] text-white/40 mb-1">Disponibilità</p>
                  <p class="text-2xl font-bold text-white">24/7</p>
                  <p class="text-[10px] text-white/40 mt-0.5">chatbot attivo</p>
                </div>
                <div class="rounded-xl bg-white/[0.03] border border-white/[0.06] p-4">
                  <p class="text-[10px] text-white/40 mb-1">Soddisfazione</p>
                  <p class="text-2xl font-bold text-green-400">91%</p>
                  <p class="text-[10px] text-green-400/70 mt-0.5">clienti soddisfatti</p>
                </div>
              </div>

              <!-- Mini chat mockup -->
              <div class="rounded-xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
                <div class="flex items-center gap-2 px-3 py-2.5 bg-white/[0.04] border-b border-white/[0.06]">
                  <div class="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <span class="text-[9px] font-bold text-blue-400">AI</span>
                  </div>
                  <span class="text-[11px] font-medium text-white/70">Assistente TisaLabs</span>
                  <div class="ml-auto flex items-center gap-1">
                    <div class="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                    <span class="text-[9px] text-white/30">Online</span>
                  </div>
                </div>
                <div class="p-3 space-y-2">
                  <div class="flex justify-end">
                    <div class="bg-blue-500/30 rounded-lg rounded-tr-sm px-3 py-2 max-w-[80%]">
                      <p class="text-[10px] text-white/80">Come posso integrare la vostra API?</p>
                    </div>
                  </div>
                  <div class="flex justify-start">
                    <div class="bg-white/[0.05] border border-white/[0.06] rounded-lg rounded-tl-sm px-3 py-2 max-w-[85%]">
                      <p class="text-[10px] text-white/60">Certo! La nostra REST API è documentata su docs.tisalabs.com. Posso guidarti step by step 🚀</p>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      <!-- CTA -->
      <div class="text-center mt-14 anim-el d6">
        <p class="text-white/40 mb-6 text-sm">{{ t('useCases.readyToTransform') }}</p>
        <router-link
          to="/contact"
          class="inline-flex items-center h-14 px-8 bg-white text-gray-900 rounded-xl font-bold text-lg transition-all duration-300 hover:bg-white/90 hover:scale-[1.02]"
        >
          {{ t('useCases.cta') }}
          <ArrowRight class="ml-2 h-5 w-5" />
        </router-link>
      </div>

    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { MessageSquare, Search, Shield, ArrowRight } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const sectionEl = ref(null)
const isVisible = ref(false)
let observer = null

const cTraffic = ref(0)
const cCost = ref(0)
const cChats = ref(0)

function countUp(target, end, duration) {
  const start = performance.now()
  function tick(now) {
    const p = Math.min((now - start) / duration, 1)
    const eased = 1 - Math.pow(1 - p, 3)
    target.value = Math.round(eased * end)
    if (p < 1) requestAnimationFrame(tick)
  }
  requestAnimationFrame(tick)
}

watch(isVisible, (v) => {
  if (!v) return
  countUp(cTraffic, 340, 1800)
  countUp(cCost, 65, 1600)
  countUp(cChats, 12, 1400)
})

onMounted(() => {
  observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) isVisible.value = true
    })
  }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' })
  if (sectionEl.value) observer.observe(sectionEl.value)
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})

const innovations = [
  { key: 'chatbot', icon: MessageSquare },
  { key: 'seo', icon: Search },
  { key: 'content', icon: Shield }
]
</script>

<style scoped>
.tabular-nums { font-variant-numeric: tabular-nums; }

.anim-el {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.7s cubic-bezier(.22,1,.36,1), transform 0.7s cubic-bezier(.22,1,.36,1);
}
.is-visible .anim-el { opacity: 1; transform: translateY(0); }
.d1 { transition-delay: 0.05s; }
.d2 { transition-delay: 0.12s; }
.d3 { transition-delay: 0.20s; }
.d4 { transition-delay: 0.28s; }
.d5 { transition-delay: 0.36s; }
.d6 { transition-delay: 0.44s; }

.particle {
  position: absolute;
  bottom: -10px;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: rgba(96, 165, 250, 0.35);
  animation: particleRise linear infinite;
}
@keyframes particleRise {
  0% { transform: translateY(0) translateX(0); opacity: 0; }
  5% { opacity: 0.7; }
  50% { transform: translateY(-50vh) translateX(20px); opacity: 0.35; }
  100% { transform: translateY(-100vh) translateX(-10px); opacity: 0; }
}
</style>
